import { AfterViewInit, Component, computed, DestroyRef, effect, inject, input, model, output, signal, untracked, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { MemberModel } from '../../../../core/models/data/member.model';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { NavItem } from '../../../../core/models/ui/profile-nav-item.model';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { MemberService } from '../../../../core/services/member.service';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';
import { PermissionUtilities } from '../../../../core/utilities/permission.utilities';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { StatusBadgeComponent } from '../../../../parts/status-badge/status-badge.component';
import { CountryCodePipe, PhoneExtensionPipe, PhonePipe } from '../../../../core/pipes/phone-number/phone-number.pipe';
import { MemberStatusActions, MemberStatuses } from '../../../../core/enums/data.enums';
import { MembersMessagingConstants } from '../../../../core/constants/messaging.constants';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { AuthorizationService } from '../../../../core/services/authorization/authorization.service';
import { AuthorizationStateService } from '../../../../core/services/authorization/authorization-state.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { MemberPermission } from '../../../../core/enums/permissions.enums';
import { IfHasChildPermissionDirective } from '../../../../core/directives/if-has-child-permission/if-has-child-permission.directive';
import { ResponsiveTabsComponent } from '../../../../parts/responsive-tabs/responsive-tabs.component';

@Component({
  selector: 'ms-member-profile-header',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, AvatarComponent, ConfirmationModalComponent, InfoModalComponent, StatusBadgeComponent, PhonePipe, PhoneExtensionPipe, CountryCodePipe, TooltipDirective, IfHasChildPermissionDirective, ResponsiveTabsComponent],
  templateUrl: './member-profile-header.component.html',
})
export class MemberProfileHeaderComponent implements AfterViewInit {
    private phonePipe = new PhonePipe();
    private countryCodePipe = new CountryCodePipe();
    private phoneExtensionPipe = new PhoneExtensionPipe();

    private authorizationService = inject(AuthorizationService);
    private authorizationStateService = inject(AuthorizationStateService);
    private userSessionService = inject(UserSessionService);

    public member = model<MemberModel | null>();

    // True when the viewed member is the logged-in user; used to hide self deactivate/delete.
    public isOwnAccount = computed<boolean>(() => {
        const m = this.member();
        return !!m && MemberUtilities.belongsToCurrentUser(m, this.userSessionService);
    });

    // Staff (user-level members.status/delete) may act on their own account; org-scoped members may not.
    public canDeactivateOwnAccount = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
        MemberPermission.Status, this.authorizationService, this.authorizationStateService));
    public canDeleteOwnAccount = computed<boolean>(() => PermissionUtilities.hasUserLevelPermission(
        MemberPermission.Delete, this.authorizationService, this.authorizationStateService));
    public canManageOwnAccount = computed<boolean>(() => this.canDeactivateOwnAccount() || this.canDeleteOwnAccount());

    // The viewed member's authority level, fetched via their own authorization context whenever the
    // viewed member changes — undefined while loading or if they have no linked user yet (invited
    // but not accepted). Populated by the constructor's effect below.
    private targetAuthorityLevel = signal<number | undefined>(undefined);

    // True while the fetch above is in flight (own account excluded — that resolves synchronously
    // with no fetch). Drives a disabled/spinner state on the protected actions so none of them ever
    // look clickable before we actually know the answer.
    private isLoadingTargetAuthorityLevel = signal<boolean>(false);
    public isCheckingProtection = computed<boolean>(() => this.isLoadingTargetAuthorityLevel());

    // The viewed member cannot be deactivated/deleted/edited by a viewer whose authority level
    // isn't stronger than theirs (DEV-2541).
    public isProtectedFromModification = computed<boolean>(() => PermissionUtilities.isProtectedFromMemberModification(
        this.targetAuthorityLevel(), this.member()?.organizationId ?? 0, this.authorizationStateService));

    public canEditProfile = computed<boolean>(() => {
        const member = this.member();
        if (!member) return false;

        if (member.userId && this.userSessionService.isCurrentUser(member.userId)) {
            return true;
        }

        const authState = this.authorizationStateService.authorizationState();
        if (!authState) return false;

        // The member's own organizationId, not the viewer's currently active organization — the
        // backend's own HasPermissionAsync check is keyed off member.OrganizationId, and viewing a
        // member's profile doesn't require that member's org to be the viewer's active one.
        return this.authorizationService.hasPermission(authState, MemberPermission.Update, member.organizationId);
    });

    // General "does the acting user hold this permission at all" checks (org- or user-level) — a
    // gap here means the option is not applicable to this user and should be hidden entirely,
    // distinct from the target-specific authority-level check below, which disables rather than
    // hides (DEV-2541).
    private hasStatusPermission = computed<boolean>(() => {
        const authState = this.authorizationStateService.authorizationState();
        if (!authState) return false;
        return this.authorizationService.hasPermission(authState, MemberPermission.Status, this.member()?.organizationId);
    });
    private hasDeletePermission = computed<boolean>(() => {
        const authState = this.authorizationStateService.authorizationState();
        if (!authState) return false;
        return this.authorizationService.hasPermission(authState, MemberPermission.Delete, this.member()?.organizationId);
    });

    // Each dropdown item's visibility, named so the template condition and the
    // ifHasChildPermission evaluator below share a single source of truth rather than
    // duplicating these expressions in two places. Visibility reflects only whether the action is
    // applicable to this user at all (general permission, status, self-account rules) — the
    // authority-level protection check does NOT affect visibility, only the disabled state applied
    // in the template (DEV-2541).
    public canShowEditProfile = computed<boolean>(() => this.canEditProfile());
    public canShowReactivate = computed<boolean>(() => this.member()?.status === MemberStatuses.Deactivated && this.hasStatusPermission());
    public canShowDeactivate = computed<boolean>(() =>
        this.member()?.status === MemberStatuses.Active
        && this.hasStatusPermission()
        && (!this.isOwnAccount() || this.canDeactivateOwnAccount()));
    public canShowDelete = computed<boolean>(() => this.hasDeletePermission() && (!this.isOwnAccount() || this.canDeleteOwnAccount()));

    public fullName = computed(() => {
        const m = this.member();
        if (!m) return '';
        return `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim();
    });

    public formattedPhone = computed(() => {
        const m = this.member();
        if (!m?.phoneNumber) return '';
        const countryCode = this.countryCodePipe.transform(m.phoneCountryCode);
        const number = this.phonePipe.transform(m.phoneNumber);
        const extension = this.phoneExtensionPipe.transform(m.phoneExtension);
        return [countryCode, number, extension].filter(Boolean).join(' ');
    });
    public isLoading = input(false);
    public navItems = input<NavItem[]>();
    public tabChanged = output<string>();
    public memberUpdated = output<void>();
    public editProfileRequested = output<void>();
    public sendInviteRequested = output<void>();

    public avatarSize = AvatarConstants.SIZE().PROFILE_PAGE;
    public avatarShape = AvatarConstants.SHAPE().PROFILE_PAGE;
    public avatarAdditionalAttributes = AvatarConstants.ADDITIONAL_ATTRIBUTES().PROFILE_PAGE;

    public MemberStatuses = MemberStatuses;
    public MemberStatusActions = MemberStatusActions;
    public isSubmitting = false;

    private memberStatusAction?: MemberStatusActions;
    public confirmationConfirmFunction: Function = () => {};
    public confirmationCancelFunction: Function = () => {};

    @ViewChild('memberProfileHeaderConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
    @ViewChild('memberProfileHeaderInfoModalRef') infoModalRef!: InfoModalComponent;
    @ViewChild(IfHasChildPermissionDirective) private actionsDropdownVisibility!: IfHasChildPermissionDirective;

    private destroyRef = inject(DestroyRef);

    constructor(
        private bootstrapService: BootstrapService,
        private memberService: MemberService
    ) {
        // Refetch the viewed member's authority level whenever the viewed member changes. Guarded by
        // memberId so a slow, stale fetch from a previously-viewed member can't overwrite a newer one.
        // Uses the member's own organizationId, not the viewer's currently active organization —
        // see isProtectedFromMemberModification for why these can diverge.
        effect(() => {
            const member = this.member();
            const memberId = member?.memberId;

            untracked(() => {
                this.targetAuthorityLevel.set(undefined);

                if (!member?.userId || this.isOwnAccount()) {
                    this.isLoadingTargetAuthorityLevel.set(false);
                    return;
                }

                this.isLoadingTargetAuthorityLevel.set(true);
                PermissionUtilities.fetchTargetMemberAuthorityLevel(member.userId, member.organizationId, this.authorizationService)
                    .then(level => {
                        if (this.member()?.memberId === memberId) {
                            this.targetAuthorityLevel.set(level);
                            this.isLoadingTargetAuthorityLevel.set(false);
                        }
                    });
            });
        });
    }

    // Registers with ifHasChildPermission (on the dropdown wrapper in the template) so the whole
    // Actions button hides itself once every item is hidden — e.g. a fully protected member —
    // instead of opening onto an empty menu (DEV-2541).
    public ngAfterViewInit(): void {
        const evaluator = () => this.canShowEditProfile() || this.shouldShowInviteButton()
            || this.canShowReactivate() || this.canShowDeactivate() || this.canShowDelete();

        this.actionsDropdownVisibility.register(evaluator);
        this.destroyRef.onDestroy(() => this.actionsDropdownVisibility.unregister(evaluator));
    }

    public onTabClick(tabId: string): void {
        this.tabChanged.emit(tabId);
    }

    public shouldShowInviteButton(): boolean {
        const member = this.member();
        if (!member) return false;
        // Invite is only meaningful for members who haven't been invited yet (Created)
        // or whose previous invite lapsed (Expired). Once Invited/Active/Deactivated/
        // Suspended/Deleted, this dropdown item is not applicable.
        return member.status === MemberStatuses.Created || member.status === MemberStatuses.Expired;
    }

    // #region Status Actions
    public confirmMemberStatusUpdate(statusAction: MemberStatusActions): void {
        const member = this.member();
        if (!member) return;

        let actionText = "";
        switch (statusAction) {
            case MemberStatusActions.Activate: 
                actionText = "activate";
                break;
            case MemberStatusActions.Deactivate: 
                actionText = "deactivate";
                break;
            case MemberStatusActions.Suspend: 
                actionText = "suspend";
                break;
            case MemberStatusActions.Unsuspend: 
                actionText = "unsuspend";
                break;
        }

        const confirmationMessageData = {actionText: actionText, firstName: member.firstName, lastName: member.lastName};
        const confirmationMessage = MembersMessagingConstants.MEMBERS_UPDATE_STATUS_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
        
        this.memberStatusAction = statusAction;
        this.confirmationConfirmFunction = this.confirmedMemberStatusConfirmation;
        this.confirmationCancelFunction = this.cancelMemberStatusConfirmation;

        this.confirmationModalRef.modalType.set(ModalTypes.Question);
        this.confirmationModalRef.message.set(confirmationMessage);
        this.confirmationModalRef.enableNotes.set(true);    
        this.confirmationModalRef.isNoteRequired.set(false);

        this.bootstrapService.manageModal("#memberProfileHeaderConfirmationModal", ModalActions.Show);
    }

    public confirmedMemberStatusConfirmation = async (note: string | undefined) => {
        const member = this.member();
        if (!member) return;

        this.isSubmitting = true;
        try {
            const response = await this.memberService.updateMemberStatus(this.memberStatusAction!, member, note);

            if (response.success === false) {
                this.infoModalRef.modalType.set(ModalTypes.Failure);
            } else {
                this.infoModalRef.modalType.set(ModalTypes.Success);
                this.memberUpdated.emit();
            }

            this.infoModalRef.message.set(response.message);
            this.bootstrapService.manageModal("#memberProfileHeaderInfoModal", ModalActions.Show);
            this.bootstrapService.manageModal("#memberProfileHeaderConfirmationModal", ModalActions.Hide);
        } finally {
            this.isSubmitting = false;
        }
    }
    
    public cancelMemberStatusConfirmation = () => {
        this.bootstrapService.manageModal("#memberProfileHeaderConfirmationModal", ModalActions.Hide);
    }
    // #endregion Status Actions

    // #region Delete Member
    public confirmMemberDeletion(): void {
        const member = this.member();
        if (!member) return;

        const confirmationMessageData = {firstName: member.firstName, lastName: member.lastName};
        const confirmationMessage = MembersMessagingConstants.MEMBERS_DELETE_CONFIRMATION().replaceValuesWithObject(confirmationMessageData);
        
        this.confirmationConfirmFunction = this.confirmedMemberDeletionConfirmation;
        this.confirmationCancelFunction = this.cancelMemberDeletionConfirmation;

        this.confirmationModalRef.modalType.set(ModalTypes.Question);
        this.confirmationModalRef.message.set(confirmationMessage);
        this.confirmationModalRef.enableNotes.set(true);    
        this.confirmationModalRef.isNoteRequired.set(false);

        this.bootstrapService.manageModal("#memberProfileHeaderConfirmationModal", ModalActions.Show);
    }

    public confirmedMemberDeletionConfirmation = async (note: string | undefined) => {
        const member = this.member();
        if (!member) return;

        this.isSubmitting = true;
        const response = await this.memberService.deleteMember(member.memberId, note);

        if (response.success === false) {
            this.infoModalRef.modalType.set(ModalTypes.Failure);
        } else {
            this.infoModalRef.modalType.set(ModalTypes.Success);
            this.memberUpdated.emit();
        }
        
        this.isSubmitting = false;
        this.infoModalRef.message.set(response.message);
        this.bootstrapService.manageModal("#memberProfileHeaderInfoModal", ModalActions.Show);
        this.bootstrapService.manageModal("#memberProfileHeaderConfirmationModal", ModalActions.Hide);
    }

    public cancelMemberDeletionConfirmation = () => {
        this.bootstrapService.manageModal("#memberProfileHeaderConfirmationModal", ModalActions.Hide);
    }
    // #endregion Delete Member
}
